import {
  BottomSheet,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCallback } from "react";
import { useRouteMatch } from "react-router-dom";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { LoadingButton } from "../../components/LoadingButton";
import { type ShiftBlockWithMappedDetails } from "./api/useFetchPaginatedShiftBlocks";
import { useRequestToBlockShiftBlock } from "./api/useRequestToBookShiftBlock";
import { SHIFT_BLOCK_ACTIONS } from "./utils";

interface Props {
  modalState: UseModalState;
  shifts: ShiftBlockWithMappedDetails["shifts"];
  shiftBlockId: string;
  agentId: string;
  facilityId: string;
}

export function RequestToBookShiftBlockBottomSheet(props: Props) {
  const { modalState, shifts, shiftBlockId, agentId, facilityId } = props;
  const { showErrorToast, showSuccessToast } = useToast();
  const { path: currentRouterUrl } = useRouteMatch();
  const {
    mutateAsync: mutateRequestToBookShiftBlock,
    isLoading: isLoadingRequestToBookShiftBlock,
  } = useRequestToBlockShiftBlock({
    shiftBlockId,
  });

  const handleCancelClicked = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.DISCARD_REQUEST_THIS_BLOCK_TAPPED,
      shiftBlockId,
      facilityId,
      totalShifts: shifts.length,
      location: currentRouterUrl,
    });
    modalState.closeModal();
  }, [currentRouterUrl, facilityId, modalState, shiftBlockId, shifts.length]);

  const handleRequestClicked = useCallback(async () => {
    try {
      await mutateRequestToBookShiftBlock({
        data: {
          type: "shift-block-booking-request",
          attributes: {
            agentId,
          },
        },
      });
      logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
        action: SHIFT_BLOCK_ACTIONS.CONFIRM_REQUEST_THIS_BLOCK_TAPPED,
        shiftBlockId,
        facilityId,
        totalShifts: shifts.length,
        location: currentRouterUrl,
      });
      showSuccessToast("We've received your request. We'll be in touch with you shortly!");
    } catch {
      showErrorToast("Something went wrong while requesting to book the shift block.");
    } finally {
      modalState.closeModal();
    }
  }, [
    agentId,
    currentRouterUrl,
    facilityId,
    modalState,
    mutateRequestToBookShiftBlock,
    shiftBlockId,
    shifts.length,
    showErrorToast,
    showSuccessToast,
  ]);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="horizontal">
          <Button fullWidth variant="outlined" size="large" onClick={handleCancelClicked}>
            Cancel
          </Button>
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isLoadingRequestToBookShiftBlock}
            onClick={handleRequestClicked}
          >
            Request
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={4} alignItems="center">
          <Text bold variant="h4" sx={{ textAlign: "center" }}>
            Request This Block
          </Text>
          <Illustration type="request-sent" />
          <Text align="center">
            After you request this block, we will confirm the block with the facility.
          </Text>
          <Text align="center">
            Once this block is booked, you are committed to working all shifts in the block and all
            other requests will be cancelled.
          </Text>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
